import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/utils';
import { 
  Palette, Store, ExternalLink, Copy, MessageCircle, QrCode, 
  Upload, Image, Download, Loader2, AlertCircle, Clock, 
  Power, PowerOff, Calendar, Truck, Gift, DollarSign
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FRONTEND_URL = window.location.origin;

const DEFAULT_HOURS = {
  monday: { enabled: true, open_time: '08:00', close_time: '22:00' },
  tuesday: { enabled: true, open_time: '08:00', close_time: '22:00' },
  wednesday: { enabled: true, open_time: '08:00', close_time: '22:00' },
  thursday: { enabled: true, open_time: '08:00', close_time: '22:00' },
  friday: { enabled: true, open_time: '08:00', close_time: '22:00' },
  saturday: { enabled: true, open_time: '08:00', close_time: '22:00' },
  sunday: { enabled: false, open_time: '08:00', close_time: '22:00' }
};

const DAY_LABELS = {
  monday: 'Segunda-feira',
  tuesday: 'Terça-feira',
  wednesday: 'Quarta-feira',
  thursday: 'Quinta-feira',
  friday: 'Sexta-feira',
  saturday: 'Sábado',
  sunday: 'Domingo'
};

export default function Settings() {
  const [establishment, setEstablishment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ logo: false, banner: false });
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [reopenTime, setReopenTime] = useState('');
  const [enableFreeDelivery, setEnableFreeDelivery] = useState(false);
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const qrCodeRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    whatsapp: '',
    primary_color: '#F97316',
    secondary_color: '#1E293B',
    logo_url: '',
    banner_url: '',
    is_open: true,
    is_manually_closed: false,
    reopen_time: '',
    opening_hours: DEFAULT_HOURS,
    default_delivery_fee: 0,
    free_delivery_threshold: null,
    use_delivery_zones: false,
    delivery_zones: [],
    // PIX settings
    pix_key: '',
    pix_key_type: 'cpf',
    pix_beneficiary_name: ''
  });

  const fetchEstablishment = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/establishments`);
      if (response.data.length > 0) {
        setEstablishment(response.data[0]);
        setError(null);
      } else {
        setError('no_establishment');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('fetch_error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEstablishment();
  }, [fetchEstablishment]);

  useEffect(() => {
    if (establishment) {
      setFormData({
        name: establishment.name,
        description: establishment.description,
        address: establishment.address,
        phone: establishment.phone,
        whatsapp: establishment.whatsapp || '',
        primary_color: establishment.primary_color,
        secondary_color: establishment.secondary_color,
        logo_url: establishment.logo_url,
        banner_url: establishment.banner_url,
        is_open: establishment.is_open,
        is_manually_closed: establishment.is_manually_closed || false,
        reopen_time: establishment.reopen_time || '',
        opening_hours: establishment.opening_hours || DEFAULT_HOURS,
        default_delivery_fee: establishment.default_delivery_fee || 0,
        free_delivery_threshold: establishment.free_delivery_threshold || null,
        use_delivery_zones: establishment.use_delivery_zones || false,
        delivery_zones: establishment.delivery_zones || [],
        // PIX settings
        pix_key: establishment.pix_key || '',
        pix_key_type: establishment.pix_key_type || 'cpf',
        pix_beneficiary_name: establishment.pix_beneficiary_name || ''
      });
      setEnableFreeDelivery(establishment.free_delivery_threshold != null && establishment.free_delivery_threshold > 0);
    }
  }, [establishment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        free_delivery_threshold: enableFreeDelivery ? formData.free_delivery_threshold : null
      };
      const response = await axios.put(`${API}/establishments/${establishment.id}`, dataToSend);
      setEstablishment(response.data);
      toast.success('Configurações salvas!');
    } catch (err) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseStore = async () => {
    try {
      const response = await axios.put(`${API}/establishments/${establishment.id}`, {
        is_manually_closed: true,
        reopen_time: reopenTime || null
      });
      setEstablishment(response.data);
      setFormData(prev => ({
        ...prev,
        is_manually_closed: true,
        reopen_time: reopenTime
      }));
      setShowCloseDialog(false);
      toast.success('Loja fechada! Os clientes serão notificados.');
    } catch (err) {
      toast.error('Erro ao fechar a loja');
    }
  };

  const handleOpenStore = async () => {
    try {
      const response = await axios.put(`${API}/establishments/${establishment.id}`, {
        is_manually_closed: false,
        reopen_time: null
      });
      setEstablishment(response.data);
      setFormData(prev => ({
        ...prev,
        is_manually_closed: false,
        reopen_time: ''
      }));
      toast.success('Loja aberta!');
    } catch (err) {
      toast.error('Erro ao abrir a loja');
    }
  };

  const updateOpeningHours = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      opening_hours: {
        ...prev.opening_hours,
        [day]: {
          ...prev.opening_hours[day],
          [field]: value
        }
      }
    }));
  };

  const copyLink = () => {
    const link = `${FRONTEND_URL}/loja/${establishment?.slug}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copiado!');
  };

  const handleImageUpload = async (file, type) => {
    if (!file) return;
    
    setUploading(prev => ({ ...prev, [type]: true }));
    
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      
      if (type === 'logo') {
        setFormData(prev => ({ ...prev, logo_url: imageUrl }));
      } else {
        setFormData(prev => ({ ...prev, banner_url: imageUrl }));
      }
      
      toast.success('Imagem enviada!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar imagem');
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const downloadQRCode = () => {
    const svg = qrCodeRef.current?.querySelector('svg');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const link = document.createElement('a');
      link.download = `qrcode-${establishment?.slug}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  if (loading) {
    return (
      <AdminLayout establishmentName={establishment?.name}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error === 'no_establishment') {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-[60vh] text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mb-4" />
          <h2 className="text-2xl font-bold text-slate-700 mb-2">Nenhum estabelecimento vinculado</h2>
          <p className="text-slate-500">Entre em contato com o administrador do sistema.</p>
        </div>
      </AdminLayout>
    );
  }

  const storeLink = `${FRONTEND_URL}/loja/${establishment?.slug}`;
  const isStoreClosed = formData.is_manually_closed;

  return (
    <AdminLayout establishmentName={establishment?.name}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Configurações</h1>
        <p className="text-slate-500">Personalize seu estabelecimento</p>
      </div>

      {/* Store Status Banner */}
      <Card className={`mb-6 ${isStoreClosed ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isStoreClosed ? (
                <PowerOff className="w-8 h-8 text-red-500" />
              ) : (
                <Power className="w-8 h-8 text-green-500" />
              )}
              <div>
                <h3 className={`font-semibold ${isStoreClosed ? 'text-red-700' : 'text-green-700'}`}>
                  {isStoreClosed ? 'Loja Fechada' : 'Loja Aberta'}
                </h3>
                <p className={`text-sm ${isStoreClosed ? 'text-red-600' : 'text-green-600'}`}>
                  {isStoreClosed 
                    ? formData.reopen_time 
                      ? `Previsão de reabertura: ${formData.reopen_time}` 
                      : 'Sem previsão de reabertura definida'
                    : 'Clientes podem fazer pedidos normalmente'
                  }
                </p>
              </div>
            </div>
            {isStoreClosed ? (
              <Button 
                onClick={handleOpenStore}
                className="bg-green-500 hover:bg-green-600"
                data-testid="open-store-btn"
              >
                <Power className="w-4 h-4 mr-2" />
                Abrir Loja
              </Button>
            ) : (
              <Button 
                onClick={() => setShowCloseDialog(true)}
                variant="destructive"
                data-testid="close-store-btn"
              >
                <PowerOff className="w-4 h-4 mr-2" />
                Fechar Loja
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Store Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="w-5 h-5" />
                Informações da Loja
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Nome do Estabelecimento</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  data-testid="settings-name-input"
                />
              </div>
              <div>
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  data-testid="settings-description-input"
                />
              </div>
              <div>
                <Label htmlFor="phone">Telefone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="(11) 99999-9999"
                  data-testid="settings-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="whatsapp" className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-green-600" />
                  WhatsApp (para notificações)
                </Label>
                <Input
                  id="whatsapp"
                  value={formData.whatsapp}
                  onChange={(e) => setFormData(prev => ({ ...prev, whatsapp: e.target.value }))}
                  placeholder="5511999999999"
                  data-testid="settings-whatsapp-input"
                />
              </div>
              <div>
                <Label htmlFor="address">Endereço</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  data-testid="settings-address-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Opening Hours */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Horário de Funcionamento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {Object.entries(DAY_LABELS).map(([day, label]) => {
                const dayHours = formData.opening_hours[day] || DEFAULT_HOURS[day];
                return (
                  <div 
                    key={day} 
                    className={`flex items-center gap-3 p-3 rounded-lg ${
                      dayHours.enabled ? 'bg-slate-50' : 'bg-slate-100 opacity-60'
                    }`}
                    data-testid={`hours-${day}`}
                  >
                    <Switch
                      checked={dayHours.enabled}
                      onCheckedChange={(checked) => updateOpeningHours(day, 'enabled', checked)}
                      data-testid={`hours-${day}-enabled`}
                    />
                    <span className="w-28 text-sm font-medium">{label}</span>
                    {dayHours.enabled ? (
                      <div className="flex items-center gap-2 flex-1">
                        <Input
                          type="time"
                          value={dayHours.open_time}
                          onChange={(e) => updateOpeningHours(day, 'open_time', e.target.value)}
                          className="w-28"
                          data-testid={`hours-${day}-open`}
                        />
                        <span className="text-slate-400">às</span>
                        <Input
                          type="time"
                          value={dayHours.close_time}
                          onChange={(e) => updateOpeningHours(day, 'close_time', e.target.value)}
                          className="w-28"
                          data-testid={`hours-${day}-close`}
                        />
                      </div>
                    ) : (
                      <span className="text-sm text-slate-500 italic">Fechado</span>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Delivery Fee Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Taxa de Entrega
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="delivery_fee">Taxa de Entrega Padrão</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">R$</span>
                  <Input
                    id="delivery_fee"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.default_delivery_fee}
                    onChange={(e) => setFormData(prev => ({ ...prev, default_delivery_fee: parseFloat(e.target.value) || 0 }))}
                    className="pl-10"
                    data-testid="delivery-fee-input"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Este valor será aplicado automaticamente em pedidos de entrega. Retirada no local é gratuita.
                </p>
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Gift className="w-4 h-4 text-green-600" />
                    <Label className="font-medium">Frete Grátis por Valor</Label>
                  </div>
                  <Switch
                    checked={enableFreeDelivery}
                    onCheckedChange={setEnableFreeDelivery}
                    data-testid="enable-free-delivery-switch"
                  />
                </div>
                
                {enableFreeDelivery && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <Label htmlFor="free_threshold" className="text-green-800">
                      Pedidos acima de quanto ganham frete grátis?
                    </Label>
                    <div className="relative mt-2">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">R$</span>
                      <Input
                        id="free_threshold"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.free_delivery_threshold || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, free_delivery_threshold: parseFloat(e.target.value) || 0 }))}
                        className="pl-10"
                        placeholder="Ex: 100.00"
                        data-testid="free-delivery-threshold-input"
                      />
                    </div>
                    <p className="text-xs text-green-700 mt-2">
                      Clientes com pedidos acima de {formData.free_delivery_threshold ? formatCurrency(formData.free_delivery_threshold) : 'R$ 0,00'} terão entrega gratuita.
                    </p>
                  </div>
                )}

                {!enableFreeDelivery && (
                  <p className="text-sm text-slate-500">
                    Ative para oferecer frete grátis em pedidos acima de um valor mínimo.
                  </p>
                )}
              </div>

              {/* Summary */}
              <div className="bg-slate-50 rounded-lg p-4 mt-4">
                <h4 className="font-medium text-slate-700 mb-2">Resumo das Configurações</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Taxa de entrega: <strong>{formatCurrency(formData.default_delivery_fee || 0)}</strong></li>
                  <li>• Retirada no local: <strong className="text-green-600">Grátis</strong></li>
                  {enableFreeDelivery && formData.free_delivery_threshold > 0 && (
                    <li>• Frete grátis: <strong className="text-green-600">Pedidos acima de {formatCurrency(formData.free_delivery_threshold)}</strong></li>
                  )}
                </ul>
              </div>

              {/* Delivery Zones */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="font-medium">Taxa por Região/Bairro</Label>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Configure taxas diferentes para cada bairro ou região
                    </p>
                  </div>
                  <Switch
                    checked={formData.use_delivery_zones}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, use_delivery_zones: checked }))}
                    data-testid="use-delivery-zones-switch"
                  />
                </div>
                
                {formData.use_delivery_zones && (
                  <div className="space-y-4">
                    {/* Add Zone Button */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newZone = {
                          id: Date.now().toString(),
                          name: `Zona ${formData.delivery_zones.length + 1}`,
                          fee: formData.default_delivery_fee || 5,
                          neighborhoods: []
                        };
                        setFormData(prev => ({
                          ...prev,
                          delivery_zones: [...prev.delivery_zones, newZone]
                        }));
                      }}
                      className="w-full"
                      data-testid="add-zone-btn"
                    >
                      + Adicionar Zona de Entrega
                    </Button>

                    {/* Zones List */}
                    {formData.delivery_zones.map((zone, index) => (
                      <div key={zone.id} className="border rounded-lg p-4 space-y-3 bg-slate-50">
                        <div className="flex items-center justify-between">
                          <Input
                            value={zone.name}
                            onChange={(e) => {
                              const updated = [...formData.delivery_zones];
                              updated[index].name = e.target.value;
                              setFormData(prev => ({ ...prev, delivery_zones: updated }));
                            }}
                            className="font-medium bg-white w-48"
                            placeholder="Nome da zona"
                            data-testid={`zone-name-${index}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                delivery_zones: prev.delivery_zones.filter((_, i) => i !== index)
                              }));
                            }}
                          >
                            Remover
                          </Button>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Label className="text-sm whitespace-nowrap">Taxa:</Label>
                          <div className="relative w-32">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">R$</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={zone.fee}
                              onChange={(e) => {
                                const updated = [...formData.delivery_zones];
                                updated[index].fee = parseFloat(e.target.value) || 0;
                                setFormData(prev => ({ ...prev, delivery_zones: updated }));
                              }}
                              className="pl-10 h-9 bg-white"
                              data-testid={`zone-fee-${index}`}
                            />
                          </div>
                        </div>

                        <div>
                          <Label className="text-sm">Bairros (separados por vírgula)</Label>
                          <Textarea
                            value={zone.neighborhoods.join(', ')}
                            onChange={(e) => {
                              const updated = [...formData.delivery_zones];
                              updated[index].neighborhoods = e.target.value
                                .split(',')
                                .map(n => n.trim())
                                .filter(n => n);
                              setFormData(prev => ({ ...prev, delivery_zones: updated }));
                            }}
                            placeholder="Centro, Jardim das Flores, Vila Nova..."
                            className="mt-1 bg-white"
                            rows={2}
                            data-testid={`zone-neighborhoods-${index}`}
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            {zone.neighborhoods.length} bairro(s) configurado(s)
                          </p>
                        </div>
                      </div>
                    ))}

                    {formData.delivery_zones.length === 0 && (
                      <div className="text-center py-6 text-slate-500 border-2 border-dashed rounded-lg">
                        <Truck className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                        <p className="text-sm">Nenhuma zona configurada</p>
                        <p className="text-xs">Clique em "Adicionar Zona" para criar regiões com taxas diferentes</p>
                      </div>
                    )}

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                      <strong>Como funciona?</strong>
                      <p className="mt-1">
                        Quando o cliente informar o endereço, o sistema verificará se o bairro corresponde a alguma zona configurada.
                        Se encontrar, aplicará a taxa da zona. Caso contrário, usará a taxa padrão.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* PIX Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Configurações PIX
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 mb-4">
                <p className="font-medium">Como funciona o PIX?</p>
                <p className="mt-1">
                  Quando o cliente escolher PIX, verá sua chave PIX + QR Code para pagamento.
                  Após pagar, ele enviará o comprovante pelo WhatsApp e você confirma no sistema.
                </p>
              </div>

              <div>
                <Label htmlFor="pix_key_type">Tipo de Chave PIX</Label>
                <Select
                  value={formData.pix_key_type || 'cpf'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, pix_key_type: value }))}
                >
                  <SelectTrigger className="mt-1" data-testid="pix-key-type">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cpf">CPF</SelectItem>
                    <SelectItem value="cnpj">CNPJ</SelectItem>
                    <SelectItem value="email">E-mail</SelectItem>
                    <SelectItem value="phone">Telefone</SelectItem>
                    <SelectItem value="random">Chave Aleatória</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="pix_key">Chave PIX</Label>
                <Input
                  id="pix_key"
                  value={formData.pix_key || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, pix_key: e.target.value }))}
                  placeholder={
                    formData.pix_key_type === 'cpf' ? '000.000.000-00' :
                    formData.pix_key_type === 'cnpj' ? '00.000.000/0000-00' :
                    formData.pix_key_type === 'email' ? 'email@exemplo.com' :
                    formData.pix_key_type === 'phone' ? '(00) 00000-0000' :
                    'Chave aleatória'
                  }
                  className="mt-1"
                  data-testid="pix-key-input"
                />
              </div>

              <div>
                <Label htmlFor="pix_beneficiary_name">Nome do Beneficiário</Label>
                <Input
                  id="pix_beneficiary_name"
                  value={formData.pix_beneficiary_name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, pix_beneficiary_name: e.target.value }))}
                  placeholder="Nome que aparecerá no PIX"
                  className="mt-1"
                  data-testid="pix-beneficiary-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Nome que o cliente verá ao pagar
                </p>
              </div>

              {formData.pix_key && (
                <div className="border rounded-lg p-4 bg-slate-50">
                  <p className="text-sm font-medium mb-2">Preview do QR Code PIX:</p>
                  <div className="flex justify-center">
                    <QRCodeSVG 
                      value={formData.pix_key}
                      size={150}
                      level="M"
                    />
                  </div>
                  <p className="text-xs text-center text-slate-500 mt-2">
                    Chave: {formData.pix_key}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Appearance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Aparência
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="primary_color">Cor Principal</Label>
                <div className="flex gap-2">
                  <Input
                    id="primary_color"
                    type="color"
                    value={formData.primary_color}
                    onChange={(e) => setFormData(prev => ({ ...prev, primary_color: e.target.value }))}
                    className="w-16 h-10 p-1"
                    data-testid="settings-primary-color"
                  />
                  <Input
                    value={formData.primary_color}
                    onChange={(e) => setFormData(prev => ({ ...prev, primary_color: e.target.value }))}
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="secondary_color">Cor Secundária</Label>
                <div className="flex gap-2">
                  <Input
                    id="secondary_color"
                    type="color"
                    value={formData.secondary_color}
                    onChange={(e) => setFormData(prev => ({ ...prev, secondary_color: e.target.value }))}
                    className="w-16 h-10 p-1"
                    data-testid="settings-secondary-color"
                  />
                  <Input
                    value={formData.secondary_color}
                    onChange={(e) => setFormData(prev => ({ ...prev, secondary_color: e.target.value }))}
                    className="flex-1"
                  />
                </div>
              </div>
              
              {/* Logo Upload */}
              <div>
                <Label className="flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Logo
                </Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={formData.logo_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, logo_url: e.target.value }))}
                    placeholder="https://... ou faça upload"
                    className="flex-1"
                    data-testid="settings-logo-input"
                  />
                  <input
                    type="file"
                    ref={logoInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e.target.files[0], 'logo')}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploading.logo}
                  >
                    {uploading.logo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  </Button>
                </div>
                {formData.logo_url && (
                  <div className="mt-2 w-20 h-20 rounded-lg overflow-hidden border">
                    <img src={formData.logo_url} alt="Logo" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
              
              {/* Banner Upload */}
              <div>
                <Label className="flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Banner
                </Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={formData.banner_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, banner_url: e.target.value }))}
                    placeholder="https://... ou faça upload"
                    className="flex-1"
                    data-testid="settings-banner-input"
                  />
                  <input
                    type="file"
                    ref={bannerInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e.target.files[0], 'banner')}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => bannerInputRef.current?.click()}
                    disabled={uploading.banner}
                  >
                    {uploading.banner ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  </Button>
                </div>
                {formData.banner_url && (
                  <div className="mt-2 h-24 rounded-lg overflow-hidden border">
                    <img src={formData.banner_url} alt="Banner" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>

              {/* Preview */}
              <div className="mt-4 p-4 rounded-lg border" style={{ backgroundColor: formData.secondary_color }}>
                <p className="text-white text-sm mb-2">Preview das cores:</p>
                <div className="flex gap-2">
                  <div className="px-4 py-2 rounded" style={{ backgroundColor: formData.primary_color }}>
                    <span className="text-white text-sm font-medium">Botão</span>
                  </div>
                  <div className="px-4 py-2 rounded bg-white" style={{ color: formData.primary_color }}>
                    <span className="text-sm font-medium">Texto</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Store Link */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ExternalLink className="w-5 h-5" />
                Link da Loja
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    value={storeLink}
                    readOnly
                    className="flex-1 bg-slate-50"
                    data-testid="store-link-input"
                  />
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={copyLink} data-testid="copy-link-btn">
                      <Copy className="w-4 h-4 mr-2" />
                      Copiar
                    </Button>
                    <Button type="button" variant="outline" onClick={() => window.open(storeLink, '_blank')} data-testid="open-store-link-btn">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Abrir
                    </Button>
                  </div>
                </div>
                
                {/* QR Code */}
                <div className="flex flex-col items-center p-4 bg-slate-50 rounded-lg" data-testid="qr-code-section">
                  <div className="flex items-center gap-2 mb-3">
                    <QrCode className="w-5 h-5 text-slate-600" />
                    <span className="font-medium text-slate-700">QR Code</span>
                  </div>
                  <div ref={qrCodeRef} className="bg-white p-4 rounded-lg shadow-sm">
                    <QRCodeSVG 
                      value={storeLink} 
                      size={150}
                      level="H"
                      includeMargin={true}
                      fgColor={formData.secondary_color}
                    />
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    className="mt-3"
                    onClick={downloadQRCode}
                    data-testid="download-qr-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Baixar QR Code
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-6 flex justify-end">
          <Button type="submit" className="bg-red-500 hover:bg-red-600" disabled={saving} data-testid="save-settings-btn">
            {saving ? 'Salvando...' : 'Salvar Configurações'}
          </Button>
        </div>
      </form>

      {/* Close Store Dialog */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent data-testid="close-store-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <PowerOff className="w-5 h-5" />
              Fechar Estabelecimento
            </DialogTitle>
            <DialogDescription>
              Ao fechar a loja, os clientes não poderão finalizar pedidos. 
              Eles ainda poderão visualizar o cardápio.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="reopen_time" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Previsão de Reabertura (opcional)
              </Label>
              <Input
                id="reopen_time"
                value={reopenTime}
                onChange={(e) => setReopenTime(e.target.value)}
                placeholder="Ex: Amanhã às 08:00, Segunda-feira, etc."
                className="mt-1"
                data-testid="reopen-time-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Esta informação será exibida aos clientes na loja.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <p className="font-medium">O que acontece ao fechar:</p>
              <ul className="list-disc list-inside mt-1 text-amber-700">
                <li>Clientes verão aviso de loja fechada</li>
                <li>Botão de finalizar pedido será desabilitado</li>
                <li>Cardápio continua visível para consulta</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCloseStore}
              variant="destructive"
              data-testid="confirm-close-store-btn"
            >
              Fechar Loja Agora
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
